import React, { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { IntroContent } from './components/IntroContent';
import { ContentCards } from './components/ContentCards';
import { DetailState } from './components/DetailState';
import { QuoteModal } from './components/quote/QuoteModal';
import { TOKENS } from './lib/designTokens';
import { loadLiveContent } from './config/contentSource';
import { setOverrides, loadFileOverrides, getUiSettings } from './config/overrides';
import { FluidCursorLayer } from './components/effects/FluidCursorLayer';

// Lazy-load the admin editor — not included in the public bundle
const EditorApp = React.lazy(() => import('./admin/EditorApp'));

// Load bundled overrides immediately (empty by default).
// Live overrides from R2 replace these once fetched.
loadFileOverrides();

// Set favicon based on route (/admin vs public)
(function setFavicon() {
  const isAdmin = window.location.pathname.startsWith('/admin');
  const href = isAdmin ? '/favicon-admin.png' : '/favicon-site.png';
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
})();

function App() {
  // Route: /admin renders the editor, everything else renders the public site
  if (window.location.pathname === '/admin') {
    return (
      <Suspense fallback={<div style={{ background: '#0a0a0c', color: '#555', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'SF Pro Display', 'InterVar', 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif" }}>Loading editor...</div>}>
        <EditorApp />
      </Suspense>
    );
  }
  // States: 'intro' | 'entered' | 'detailVideo' | 'detailWeb'
  const [appState, setAppState] = useState('intro');
  const [portfolio, setPortfolio] = useState(null);
  const [quoteContext, setQuoteContext] = useState(null); // null=closed, 'video'|'web'|'general'
  const scrollAccum = useRef(0);
  const scrollTimeout = useRef(null);
  const canNavigate = useRef(true);

  const entered = appState === 'entered';
  const isIntro = appState === 'intro';
  const isDetail = appState === 'detailVideo' || appState === 'detailWeb';

  // Fluid cursor: desktop-only + reduced-motion detection
  const [isDesktop, setIsDesktop] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Cursor appearance (loaded from overrides once after R2 fetch, not per-frame)
  const [cursorColor, setCursorColor] = useState('#6d28ff');
  const [cursorOpacity, setCursorOpacity] = useState(0.1);

  useEffect(() => {
    const mqDesktop = window.matchMedia('(hover:hover) and (pointer:fine)');
    const mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsDesktop(mqDesktop.matches);
    setReducedMotion(mqMotion.matches);
    const onDesktop = (e) => setIsDesktop(e.matches);
    const onMotion = (e) => setReducedMotion(e.matches);
    mqDesktop.addEventListener('change', onDesktop);
    mqMotion.addEventListener('change', onMotion);
    return () => {
      mqDesktop.removeEventListener('change', onDesktop);
      mqMotion.removeEventListener('change', onMotion);
    };
  }, []);

  const fluidEnabled = isDesktop && !reducedMotion && (isIntro || entered);

  const lockNav = (ms) => {
    canNavigate.current = false;
    setTimeout(() => { canNavigate.current = true; }, ms);
  };

  const handleEnter = useCallback(() => {
    if (!canNavigate.current || appState !== 'intro') return;
    setAppState('entered');
    lockNav(700);
  }, [appState]);

  const handleBack = useCallback(() => {
    if (!canNavigate.current || appState !== 'entered') return;
    setAppState('intro');
    lockNav(700);
  }, [appState]);

  const handleOpenDetail = useCallback((variant) => {
    if (!canNavigate.current) return;
    setAppState(variant === 'video' ? 'detailVideo' : 'detailWeb');
    lockNav(700);
  }, []);

  const handleBackToEntered = useCallback(() => {
    if (!canNavigate.current) return;
    setAppState('entered');
    lockNav(700);
  }, []);

  // Quote modal: context is 'video' | 'web' | null (user picks inside modal)
  const openQuote = useCallback((ctx) => {
    setQuoteContext(ctx === null ? 'general' : ctx);
  }, []);
  const closeQuote = useCallback(() => setQuoteContext(null), []);

  // Intro CTA: transition to State 2 then open quote modal
  const handleIntroQuote = useCallback(() => {
    if (!canNavigate.current || appState !== 'intro') return;
    setAppState('entered');
    lockNav(700);
    setTimeout(() => setQuoteContext('general'), 650);
  }, [appState]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, []);

  // Load live content from R2 (portfolio + overrides) on mount
  useEffect(() => {
    loadLiveContent().then(({ portfolio, overrides }) => {
      setPortfolio(portfolio);
      if (overrides) {
        setOverrides(overrides);
        // Read cursor settings from overrides.ui.cursorFluid (set once, not per frame)
        const ui = getUiSettings();
        if (ui.cursorFluid?.color) setCursorColor(ui.cursorFluid.color);
        if (typeof ui.cursorFluid?.opacity === 'number') setCursorOpacity(ui.cursorFluid.opacity);
      }
    });
  }, []);

  // Keyboard
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const isInModal = (el) => {
      if (!el) return false;
      return !!el.closest('[data-quote-modal="true"]');
    };
    const handler = (e) => {
      // Never intercept keys when user is typing in a form field or inside a modal
      if (isTypingTarget(e.target) || isInModal(e.target)) return;
      // Never hijack Enter/Space on links (legal pages must stay reachable by keyboard)
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('a[href]')) return;
      if (e.key === 'Enter' || e.key === ' ') {
        if (isDetail) return; // Don't interfere with detail state
        e.preventDefault();
        if (isIntro) handleEnter();
        else if (entered) handleBack();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [appState, isIntro, entered, isDetail, handleEnter, handleBack]);

  // Wheel (only intro <-> entered)
  useEffect(() => {
    const handler = (e) => {
      if (!canNavigate.current || isDetail) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? 'down' : 'up';
      if ((dir === 'down' && isIntro) || (dir === 'up' && entered)) {
        scrollAccum.current += Math.abs(e.deltaY);
      } else {
        scrollAccum.current = 0;
        return;
      }
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => { scrollAccum.current = 0; }, 600);
      if (scrollAccum.current >= TOKENS.animation.scrollThreshold) {
        scrollAccum.current = 0;
        if (dir === 'down') handleEnter();
        else handleBack();
      }
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => {
      window.removeEventListener('wheel', handler);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [appState, isIntro, entered, isDetail, handleEnter, handleBack]);

  // Touch (only intro <-> entered)
  useEffect(() => {
    let startY = 0;
    const onStart = (e) => { startY = e.touches[0].clientY; };
    const onEnd = (e) => {
      if (!canNavigate.current || isDetail) return;
      const diff = startY - e.changedTouches[0].clientY;
      if (diff > 50 && isIntro) handleEnter();
      else if (diff < -50 && entered) handleBack();
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [appState, isIntro, entered, isDetail, handleEnter, handleBack]);

  // Browser back support
  useEffect(() => {
    const onPop = () => {
      if (isDetail) {
        setAppState('entered');
      } else if (entered) {
        setAppState('intro');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [appState, isDetail, entered]);

  useEffect(() => {
    if (appState !== 'intro') {
      window.history.pushState({ state: appState }, '');
    }
  }, [appState]);

  const ease = [0.4, 0, 0.2, 1];

  return (
    <div className="byzy-app font-sans" data-testid="byzy-app">
      <div className="bg-base" aria-hidden="true" />
      <video
        className="bg-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        poster={`${process.env.PUBLIC_URL}/videos/bg-gradient-poster.jpg`}
        aria-hidden="true"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      >
        <source src={`${process.env.PUBLIC_URL}/videos/bg-gradient.mp4`} type="video/mp4" />
        <source src={`${process.env.PUBLIC_URL}/videos/bg-gradient.webm`} type="video/webm" />
      </video>
      <div className="bg-noise" aria-hidden="true" />

      <motion.div
        className="glass-overlay"
        aria-hidden="true"
        initial={false}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease }}
      />

      {/* Fluid cursor overlay (State 1 + 2).
           Always mounted — never conditionally rendered — so that React never
           remounts it (which would trigger a parent re-render and black-flash the
           background video). The `enabled` prop handles pausing + opacity 0 when
           not active. color/opacity come from overrides.ui.cursorFluid. */}
      <FluidCursorLayer enabled={fluidEnabled} color={cursorColor} opacity={cursorOpacity} />

      {/* Layer: Intro — slides up + fades on exit */}
      <motion.div
        className="layer-intro"
        initial={false}
        animate={{ opacity: isIntro ? 1 : 0, y: isIntro ? 0 : -50 }}
        transition={{ duration: 0.6, ease }}
        style={{ pointerEvents: isIntro ? 'auto' : 'none' }}
      >
        <IntroContent onEnter={handleEnter} entered={!isIntro} onOpenQuote={handleIntroQuote} />
      </motion.div>

      {/* Layer: Content (State 2) — slides up from below + fades */}
      <motion.div
        className="layer-content"
        initial={false}
        animate={{
          opacity: entered ? 1 : 0,
          y: entered ? 0 : 30,
        }}
        transition={{ duration: 0.6, delay: entered ? 0.08 : 0, ease }}
        style={{ pointerEvents: entered ? 'auto' : 'none' }}
      >
        <ContentCards onOpenDetail={handleOpenDetail} onOpenQuote={openQuote} />
      </motion.div>

      {/* Layer: Detail (State 3) */}
      <motion.div
        className="layer-detail"
        initial={false}
        animate={{
          opacity: isDetail ? 1 : 0,
          y: isDetail ? 0 : 18,
        }}
        transition={{ duration: 0.45, delay: isDetail ? 0.06 : 0, ease }}
        style={{ pointerEvents: isDetail ? 'auto' : 'none' }}
      >
        {isDetail && (
          <DetailState
            variant={appState === 'detailVideo' ? 'video' : 'web'}
            onBack={handleBackToEntered}
            categoryData={portfolio?.[appState === 'detailVideo' ? 'video' : 'web']}
            onOpenQuote={openQuote}
          />
        )}
      </motion.div>

      {/* Legal links (LCEN) — visible on intro + cards, hidden on detail to keep the gallery clear */}
      <nav
        className={`legal-links${isDetail ? ' legal-links--hidden' : ''}`}
        aria-label="Informations légales"
        aria-hidden={isDetail ? 'true' : undefined}
        data-testid="legal-links"
      >
        <a href="/mentions-legales" tabIndex={isDetail ? -1 : 0}>Mentions légales</a>
        <span aria-hidden="true">·</span>
        <a href="/confidentialite" tabIndex={isDetail ? -1 : 0}>Confidentialité</a>
      </nav>

      <AnimatePresence>
        {quoteContext && (
          <QuoteModal
            context={quoteContext === 'general' ? null : quoteContext}
            onClose={closeQuote}
          />
        )}
      </AnimatePresence>

      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(18, 18, 22, 0.92)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#f0f0f2',
            backdropFilter: 'blur(14px)',
            fontFamily: "'SF Pro Display', 'InterVar', 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
